import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { FiPause, FiPlay } from "react-icons/fi";
import { useLanguage } from "../../contexts/LanguageContext";

const drift = keyframes`
  0%, 100% { transform: translate3d(0, 0, 0) rotate(var(--angle)); }
  50% { transform: translate3d(var(--drift-x, 5px), -12px, 0) rotate(calc(var(--angle) + 2deg)); }
`;

const Stage = styled.div<{ $running: boolean }>`
  position: relative;
  width: 100%;
  max-width: 620px;
  aspect-ratio: 1.08;
  isolation: isolate;

  &::before {
    content: "";
    position: absolute;
    inset: 12% 0 6%;
    border-radius: 50%;
    background: radial-gradient(
      ellipse,
      #ff985b16,
      #8badce08 46%,
      transparent 70%
    );
    pointer-events: none;
  }

  .floating {
    position: absolute;
    animation: ${drift} var(--duration, 12s) ease-in-out infinite;
    animation-delay: var(--delay, 0s);
    animation-play-state: ${({ $running }) =>
      $running ? "running" : "paused"};
    transform: rotate(var(--angle));
  }

  .dust {
    width: 64%;
    left: 8%;
    top: 29%;
    --angle: -8deg;
    --duration: 14s;
  }
  .nuke {
    width: 43%;
    left: 51%;
    top: 9%;
    --angle: 9deg;
    --duration: 16s;
    --delay: -5s;
  }
  .overpass {
    width: 44%;
    left: 53%;
    top: 64%;
    --angle: 7deg;
    --duration: 17s;
    --delay: -9s;
  }
  .dust span {
    top: 12px;
    bottom: auto;
  }
  .nuke span {
    top: 12px;
    right: 14px;
    bottom: auto;
    left: auto;
  }
  .overpass span {
    right: 14px;
    left: auto;
  }
  .ak {
    width: 65%;
    left: 0%;
    top: -3%;
    --angle: -12deg;
    --duration: 11s;
    --delay: -3s;
    --drift-x: -6px;
  }
  .m4 {
    width: 66%;
    left: 0%;
    top: 53%;
    --angle: 10deg;
    --duration: 13s;
    --delay: -7s;
    --drift-x: -5px;
  }

  @media (prefers-reduced-motion: reduce) {
    .floating {
      animation: none !important;
    }
    button {
      display: none;
    }
  }
`;

const MapCard = styled.div`
  margin: 0;
  aspect-ratio: 1.55;
  overflow: hidden;
  border: 1px solid #ffffff26;
  border-radius: 14px;
  background: #20252c;
  box-shadow: 0 18px 45px #0006;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(transparent 45%, #0c1119b8);
  }
  span {
    position: absolute;
    z-index: 1;
    bottom: 12px;
    left: 16px;
    color: #fff;
    font-size: clamp(13px, 1.5vw, 16px);
    font-weight: 600;
    letter-spacing: 0.02em;
  }
`;

const Skin = styled.img`
  z-index: 2;
  height: auto;
  pointer-events: none;
  filter: drop-shadow(0 20px 14px #0008);
`;

const MotionButton = styled.button`
  position: absolute;
  bottom: 0;
  right: 8px;
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) => theme.colors.surface};
  cursor: pointer;
  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 4px;
  }
`;

export default function LandingShowcase() {
  const { t } = useLanguage();
  const stageRef = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);
  const [visible, setVisible] = useState(true);
  const [pageVisible, setPageVisible] = useState(!document.hidden);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) =>
      setVisible(entry.isIntersecting),
    );
    if (stageRef.current) observer.observe(stageRef.current);
    const onVisibility = () => setPageVisible(!document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return (
    <Stage ref={stageRef} $running={!paused && visible && pageVisible}>
      <div aria-hidden="true">
        <MapCard className="floating nuke">
          <img
            src="/landing/nuke.jpg"
            alt=""
            width="800"
            height="484"
            decoding="async"
            draggable={false}
          />
          <span>Nuke</span>
        </MapCard>
        <MapCard className="floating dust">
          <img
            src="/landing/dust2.jpg"
            alt=""
            width="800"
            height="484"
            decoding="async"
            draggable={false}
          />
          <span>Dust II</span>
        </MapCard>
        <MapCard className="floating overpass">
          <img
            src="/landing/overpass.jpg"
            alt=""
            width="800"
            height="484"
            decoding="async"
            draggable={false}
          />
          <span>Overpass</span>
        </MapCard>
        <Skin
          className="floating ak"
          src="/landing/ak47.png"
          alt=""
          width="512"
          height="384"
          decoding="async"
          draggable={false}
        />
        <Skin
          className="floating m4"
          src="/landing/m4a1s.png"
          alt=""
          width="512"
          height="384"
          decoding="async"
          draggable={false}
        />
      </div>
      <MotionButton
        type="button"
        aria-label={paused ? t("land.motion_play") : t("land.motion_pause")}
        aria-pressed={paused}
        onClick={() => setPaused(!paused)}
      >
        {paused ? (
          <FiPlay aria-hidden="true" />
        ) : (
          <FiPause aria-hidden="true" />
        )}
      </MotionButton>
    </Stage>
  );
}
